import React, { useState } from 'react';
import './ShareModal.css';

const ShareModal = ({ isOpen, onClose, isMobile = false, imageId, imageUrl, collectionId }) => {
  const [linkCopied, setLinkCopied] = useState(false);

  if (!isOpen) return null;

  // Generate share link - ensure it's a full URL
  // Priority: collectionId > imageId > current URL
  let shareLink;
  if (collectionId) {
    shareLink = `${window.location.origin}/share/collection/${collectionId}`;
  } else if (imageId) {
    shareLink = `${window.location.origin}/share/image/${imageId}`;
  } else {
    shareLink = window.location.href;
  }
  
  // Ensure the URL is absolute and properly formatted
  const fullShareLink = shareLink.startsWith('http') ? shareLink : `${window.location.origin}${shareLink}`;

  const handleSlackClick = (e) => {
    e.stopPropagation();
    // Slack doesn't have a public share URL like Facebook
    // Best approach: Copy link to clipboard and try to open Slack
    navigator.clipboard.writeText(fullShareLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => {
        setLinkCopied(false);
      }, 3000);
      
      // Try to open Slack app (mobile) or web (desktop)
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        // Try Slack app deep link
        window.location.href = `slack://`;
        // Fallback: open Slack web
        setTimeout(() => {
          window.open('https://slack.com/', '_blank');
        }, 500);
      } else {
        // Desktop: try to open Slack web app
        // Note: This will just open Slack, user needs to paste the link manually
        window.open('https://slack.com/', '_blank');
      }
    }).catch((err) => {
      console.error('Failed to copy link:', err);
      // If clipboard fails, just try to open Slack
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        window.location.href = `slack://`;
      } else {
        window.open('https://slack.com/', '_blank');
      }
    });
    // Don't close immediately - let user see the "Copied!" message
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const handleCopyLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(fullShareLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    });
  };

  const handleFacebookShare = (e) => {
    e.stopPropagation();
    // Open Facebook share dialog with the link - this will create a new post
    // Using the standard sharer.php endpoint which should attach the link
    // The link parameter must be properly URL encoded
    const encodedLink = encodeURIComponent(fullShareLink);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`;
    
    // Open in a new window (not popup) to ensure it works
    window.open(facebookUrl, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleInstagramShare = (e) => {
    e.stopPropagation();
    // Try to open Instagram app first, then fallback to web
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      // Try Instagram app scheme
      window.location.href = `instagram://share?url=${encodeURIComponent(fullShareLink)}`;
      // Fallback to web Instagram if app doesn't open
      setTimeout(() => {
        window.open('https://www.instagram.com/', '_blank');
      }, 500);
    } else {
      // Desktop: open Instagram web
      window.open('https://www.instagram.com/', '_blank');
    }
    // Copy link to clipboard as fallback since Instagram requires manual paste
    navigator.clipboard.writeText(fullShareLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    });
    onClose();
  };

  const handleGoogleShare = (e) => {
    e.stopPropagation();
    // Google share via Gmail compose
    // Copy link to clipboard first so user can paste it
    navigator.clipboard.writeText(fullShareLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => {
        setLinkCopied(false);
      }, 3000);
    });
    
    // Open Gmail compose with subject and body
    // Note: Gmail's body parameter can be unreliable, so we copy link to clipboard as backup
    const subject = encodeURIComponent('Check out this image from KURA');
    const bodyText = `Check out this amazing image:\n\n${fullShareLink}\n\nCreated with KURA - AI-powered image generation.`;
    const body = encodeURIComponent(bodyText);
    
    // Use Gmail compose URL - the link is in the body text
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
    
    // Open Gmail compose
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
    
    // Don't close immediately - let user see the "Copied!" message
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div 
        className={`share-modal-container ${isMobile ? 'share-modal-container-mobile' : ''}`}
        onClick={stopPropagation}
      >
        <div style={{ position: 'relative' }}>
          <button
            className="share-modal-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6L18 18" />
            </svg>
          </button>
          <h3 className="share-modal-title">Share this image</h3>
        </div>
        <div className="share-modal-social-icons">
          {/* Slack */}
          <div className="share-modal-social-icon" title="Slack" onClick={handleSlackClick}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52z" />
              <path fill="#E01E5A" d="M6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" />
              <path fill="#36C5F0" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834z" />
              <path fill="#36C5F0" d="M8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" />
              <path fill="#2EB67D" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834z" />
              <path fill="#2EB67D" d="M17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" />
              <path fill="#ECB22E" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52z" />
              <path fill="#ECB22E" d="M15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
            </svg>
          </div>
          {/* Facebook */}
          <div className="share-modal-social-icon" title="Facebook" onClick={handleFacebookShare}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </div>
          {/* Instagram */}
          <div className="share-modal-social-icon" title="Instagram" onClick={handleInstagramShare}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <linearGradient id={isMobile ? "ig-gradient-mobile" : "ig-gradient"} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f09433" />
                <stop offset="25%" stopColor="#e6683c" />
                <stop offset="50%" stopColor="#dc2743" />
                <stop offset="75%" stopColor="#cc2366" />
                <stop offset="100%" stopColor="#bc1888" />
              </linearGradient>
              <path fill={`url(#${isMobile ? "ig-gradient-mobile" : "ig-gradient"})`} d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </div>
          {/* Google (Gmail) */}
          <div className="share-modal-social-icon" title="Google (Gmail)" onClick={handleGoogleShare}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          </div>
        </div>
        <p className="share-modal-separator">Or copy the link</p>
        <div className={`share-modal-link-container ${isMobile ? 'share-modal-link-container-mobile' : ''}`}>
          <input 
            type="text" 
            value={fullShareLink} 
            readOnly 
            className={`share-modal-link-input ${isMobile ? 'share-modal-link-input-mobile' : ''}`}
          />
          <button 
            className="share-modal-copy-btn" 
            onClick={handleCopyLink} 
            title={linkCopied ? "Copied!" : "Copy link"}
          >
            {linkCopied ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;

